'use client'

// ── The error card: an EOB that apologizes ───────────────────────────────────
//
// The concept: payer documents answer failure with remark codes, appeal
// windows, and 45-day silences. Ours answers with a remark-code STAMP (the
// joke), an instant admission of fault, a working retry, a Claude explanation
// in plain English, and a support email that is already written.
//
// Behavior contract:
//   · On mount it reports the crash (Sentry + POST /api/error-report, fetch
//     keepalive so a navigating tab still delivers) — before this card,
//     "the team has been notified" was only true for server errors.
//   · The report's returned uuid is the ONLY key the explain call sends —
//     never the digest (capability model, see app/api/error-explain).
//   · Every path stays actionable: unauthenticated, report failure, or model
//     failure all still leave Email support + Copy report working with
//     ref/page/time prelabeled. No dead ends on a page that is itself a
//     failure state.
//
// The braille snake is legitimate here — the explain wait IS a model call —
// and this file is registered in the ai-loader allowlist with that reason.

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import * as Sentry from '@sentry/nextjs'
import { SnakeSpinner } from '@/components/ui/ai-spinner'
import { AiGlowButton } from '@/components/ai/ai-glow'
import { buildSupportEmail, supportMailtoHref } from '@/lib/errors/support-email'

interface Explanation {
  plain: string
  technical: string
  source: 'model' | 'cache' | 'canned'
}

type ExplainState = 'idle' | 'thinking' | 'done' | 'unreachable'

export function ErrorReportCard({
  error,
  retryAction,
}: {
  error: Error & { digest?: string }
  retryAction: () => void
}) {
  const [occurrenceId, setOccurrenceId] = useState<string | null>(null)
  const [reportSettled, setReportSettled] = useState(false)
  const [explainState, setExplainState] = useState<ExplainState>('idle')
  const [explanation, setExplanation] = useState<Explanation | null>(null)
  const [page, setPage] = useState<string | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'done' | 'failed'>('idle')
  const reported = useRef(false)

  // Deterministic initial href (no Date, no window) so server- and client-
  // rendered boundary HTML agree; the effect below swaps in the full version
  // after mount (Codex fold: render-time clocks are a hydration hazard).
  const [mailtoHref, setMailtoHref] = useState(() =>
    supportMailtoHref({
      digest: error.digest ?? null,
      occurrenceId: null,
      page: null,
      atIso: '',
      plain: null,
      technical: null,
    }),
  )

  useEffect(() => {
    // One report per boundary mount (strict-mode double-effects included).
    if (reported.current) return
    reported.current = true

    Sentry.captureException(error)
    setPage(window.location.pathname)

    fetch('/api/error-report', {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        digest: error.digest ?? null,
        errorMessage: error.message?.slice(0, 2000) ?? null,
        errorStack: error.stack?.slice(0, 16000) ?? null,
        href: window.location.href,
      }),
    })
      .then(async (res) => (res.ok ? ((await res.json()) as { id?: string }) : null))
      .then((data) => setOccurrenceId(data?.id ?? null))
      .catch(() => setOccurrenceId(null))
      .finally(() => setReportSettled(true))
  }, [error])

  const explain = async () => {
    if (!occurrenceId || explainState === 'thinking') return
    setExplainState('thinking')
    try {
      const res = await fetch('/api/error-explain', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ occurrenceId }),
      })
      if (!res.ok) throw new Error(`explain ${res.status}`)
      const data = (await res.json()) as Explanation
      setExplanation(data)
      setExplainState('done')
    } catch {
      setExplainState('unreachable')
    }
  }

  useEffect(() => {
    setMailtoHref(
      supportMailtoHref({
        digest: error.digest ?? null,
        occurrenceId,
        page,
        atIso: new Date().toISOString(),
        plain: explanation?.plain ?? null,
        technical: explanation?.technical ?? null,
      }),
    )
  }, [error.digest, occurrenceId, page, explanation])

  const copyReport = async () => {
    const { subject, body } = buildSupportEmail({
      digest: error.digest ?? null,
      occurrenceId,
      page,
      atIso: new Date().toISOString(),
      plain: explanation?.plain ?? null,
      technical: explanation?.technical ?? null,
    })
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`)
      setCopyState('done')
    } catch {
      // Clipboard can be denied — say so; the mailto next to it still works.
      setCopyState('failed')
    }
    setTimeout(() => setCopyState('idle'), 2500)
  }

  return (
    <div
      role="alert"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--page-margin, 24px)',
      }}
    >
      <div className="w-full rounded-xl border border-border bg-surface" style={{ maxWidth: 560, padding: 36 }}>
        {/* The stamp row — a remark code, the way a payer would put it. */}
        <div className="flex items-center justify-between gap-3" style={{ marginBottom: 20 }}>
          <span
            className="text-l3 font-mono uppercase tracking-wider"
            style={{
              color: 'var(--sb-color-danger)',
              border: '1px dashed color-mix(in srgb, var(--sb-color-danger) 55%, transparent)',
              borderRadius: 4,
              padding: '3px 8px',
            }}
          >
            Remark code: OOPS-01
          </span>
          {error.digest && (
            <span className="text-b3 font-mono" style={{ color: 'var(--sb-color-text-placeholder)' }}>
              ref {error.digest}
            </span>
          )}
        </div>

        <h1 className="text-h2" style={{ marginBottom: 10 }}>
          This page hit a snag.
        </h1>
        <p className="text-b2 text-muted-foreground" style={{ marginBottom: 24, lineHeight: 1.55 }}>
          Unlike your payers, we won&apos;t make you appeal three times or wait 45 days. Try again —
          most retries clear on first resubmission.
        </p>

        <div className="flex items-center gap-3" style={{ marginBottom: 28, flexWrap: 'wrap', rowGap: 10 }}>
          <button
            onClick={retryAction}
            className="text-btn2 rounded-lg"
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'var(--sb-color-primary-default)',
              color: 'var(--sb-color-text-on-dark, #fff)',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <Link
            href="/"
            className="text-btn2 rounded-lg border border-border bg-surface text-secondary-foreground hover:bg-surface-subtle transition-colors"
            style={{ padding: '10px 20px', display: 'inline-flex', alignItems: 'center' }}
          >
            Go home
          </Link>
        </div>

        {/* The Claude zone. Rendered only once the report settled — the
            explain capability is the report's returned uuid. */}
        <div className="border-t border-border" style={{ paddingTop: 20 }} aria-busy={explainState === 'thinking'}>
          {explainState === 'idle' && occurrenceId && (
            <AiGlowButton onClick={explain}>Explain what happened</AiGlowButton>
          )}

          {explainState === 'idle' && reportSettled && !occurrenceId && (
            <p className="text-b3 text-muted-foreground">
              Automatic reporting didn&apos;t reach the server this time — the email below still
              carries the reference.
            </p>
          )}

          {explainState === 'thinking' && (
            <span className="inline-flex items-center gap-2 text-b3" style={{ color: 'var(--sb-color-ai-foreground)' }}>
              <SnakeSpinner fontSize={18} color="var(--sb-color-ai-foreground)" />
              Claude is reading the error…
            </span>
          )}

          {explainState === 'done' && explanation && (
            <div className="rounded-lg bg-ai-light" style={{ padding: '14px 16px' }}>
              <p className="text-l3 font-mono uppercase tracking-wider" style={{ color: 'var(--sb-color-ai-foreground)', marginBottom: 8 }}>
                What happened
              </p>
              <p className="text-b2 text-foreground" style={{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {explanation.plain}
              </p>
              <details style={{ marginTop: 10 }}>
                <summary className="text-b3 text-muted-foreground" style={{ cursor: 'pointer' }}>
                  Technical details (for support)
                </summary>
                <p className="text-b3 font-mono text-muted-foreground" style={{ marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {explanation.technical}
                </p>
              </details>
            </div>
          )}

          {explainState === 'unreachable' && (
            <p className="text-b3 text-muted-foreground">
              Claude couldn&apos;t look at this one. The email below still carries everything support needs.
            </p>
          )}

          <div className="flex items-center gap-3" style={{ marginTop: 18, flexWrap: 'wrap', rowGap: 10 }}>
            <a
              href={mailtoHref}
              className="text-btn2 rounded-lg border border-border bg-surface text-secondary-foreground hover:bg-surface-subtle transition-colors"
              style={{ padding: '8px 16px', display: 'inline-flex', alignItems: 'center' }}
            >
              Email support
            </a>
            <button
              onClick={copyReport}
              className="text-btn2 rounded-lg border border-border bg-surface text-secondary-foreground hover:bg-surface-subtle transition-colors"
              style={{ padding: '8px 16px', cursor: 'pointer' }}
            >
              {copyState === 'done' ? 'Copied' : copyState === 'failed' ? 'Copy failed — use the email' : 'Copy report'}
            </button>
            {!reportSettled && (
              <span className="text-b3" style={{ color: 'var(--sb-color-text-placeholder)' }}>
                Reporting…
              </span>
            )}
          </div>
        </div>

        <p className="text-b3 font-mono" style={{ color: 'var(--sb-color-text-placeholder)', marginTop: 22 }}>
          This is not a bill. It isn&apos;t even a claim. It&apos;s just a bug.
        </p>
      </div>
    </div>
  )
}
